import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useTransition,
} from "react";
import { produce } from "immer";
import { useStore } from "zustand";
import {
  Text,
  Image,
  AvatarGroup,
  Avatar,
  ActionButton,
  MenuTrigger,
  Menu,
  MenuItem,
  Keyboard,
  ButtonGroup,
  Button,
  CustomDialog,
  TextField,
  TextArea,
  CloseButton,
  Skeleton,
  TooltipTrigger,
  Tooltip,
  DialogContainer,
  FileTrigger,
  ProgressCircle,
} from "@react-spectrum/s2";

import SimilarIcon from "@react-spectrum/s2/icons/Similar";
import BrowseIcon from "@react-spectrum/s2/icons/Binoculars";
import MoreIcon from "@react-spectrum/s2/icons/More";
import UploadToCloudIcon from "@react-spectrum/s2/icons/UploadToCloud";
import AIGenerateIcon from "icon:@app/assets/AIGenerate.svg";

import { store } from "../../store";
import { useStory, useUpdateStory } from "@app/hooks/stories";
import { uploadFileByBlob } from "@app/services/s3";

import { PersonaAvatar } from "@app/components/PersonaAvatar";

import { StoryCardVisual } from "./StoryCardVisual";
import { ImageSelectionDialog } from "./ImageSelectionDialog";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const detailStyles = style({
  font: "detail",
  color: "neutral-subdued",
  marginTop: 0,
});

export const StoryEditDialog = ({ storyId, item }) => {
  const [prompt, setPrompt] = useState(item?.visualPrompt ?? "");
  const [selectedPersonaKeys, setSelectedPersonaKeys] = useState(
    new Set(item?.personas ?? []),
  );
  const [isBrowseDialogOpen, setIsBrowseDialogOpen] = useState(false);

  const [title, setTitle] = useState(item?.title ?? "");
  const [text, setText] = useState(item?.text ?? "");

  const storyQuery = useStory(storyId);
  const story = storyQuery.data;

  const updateStory = useUpdateStory(storyId);

  const selectedPersonas = useMemo(
    () =>
      [...selectedPersonaKeys].map((key) =>
        story?.personas?.find((p) => p.id === key),
      ),
    [story?.personas, selectedPersonaKeys],
  );

  useEffect(() => {
    setPrompt(item?.visualPrompt ?? prompt);
  }, [item?.visualPrompt]);

  const isPromptPersonasPending = useStore(
    store,
    (state) => state.isPromptPersonasPending,
  );
  const isPromptVisualsPending = useStore(
    store,
    (state) => state.isPromptVisualsPending,
  );
  const isGenerating = isPromptPersonasPending || isPromptVisualsPending;
  const isRegenerateVisualPending = useStore(
    store,
    (state) => state.isRegenerateVisualPending,
  );

  const regenerateVisual = useStore(store, (state) => state.regenerateVisual);
  const generateSimilarVisual = useStore(
    store,
    (state) => state.generateSimilarVisual,
  );
  const regenerateVisualContext = useStore(
    store,
    (state) => state.regenerateVisualContext,
  );

  const updateStoryScriptItem = useStore(
    store,
    (state) => state.updateStoryScriptItem,
  );

  const updateStoryVisualImage = useStore(
    store,
    (state) => state.updateStoryVisualImage,
  );

  const email = useStore(store, (state) => state.ims?.profile?.email);

  const browseVisuals = (story) => {
    console.log("browseVisuals", story);
    setIsBrowseDialogOpen(true);
  };

  const handleImageConfirm = (selectedImage) => {
    updateStory.mutate({
      script: story.script.map((scriptItem) => {
        return scriptItem.id === item.id
          ? {
              ...scriptItem,
              visualPrompt: selectedImage.description ?? "test",
              visuals: scriptItem.visuals?.map((visual, idx) =>
                idx === 0
                  ? {
                      ...visual,
                      type: "cc-library",
                      libraryId: selectedImage.libraryId,
                      id: selectedImage.id,
                      visualPrompt: selectedImage.description ?? "test",
                    }
                  : visual,
              ) || [
                {
                  type: "cc-library",
                  libraryId: selectedImage.libraryId,
                  id: selectedImage.id,
                  visualPrompt: selectedImage.description ?? "test",
                },
              ],
            }
          : scriptItem;
      }),
    });
    setIsBrowseDialogOpen(false);
  };

  //useStore(store, (state) => state.browseVisuals);
  const handleTextUpdate = useCallback(() => {
    store.getState().updateStoryScriptItem(storyId, item.id, { title, text });
  }, [storyId, item.id, title, text]);

  const visualsListRef = useRef(null);

  const [isUploading, startUploading] = useTransition();

  const handleImageSelect = useCallback(
    (files) => {
      if (files.length > 0) {
        startUploading(async () => {
          const [file] = files;

          const filePath = `${email.split("@")[0]}/stories/${storyId}/visuals/${file.name}`;

          const response = await uploadFileByBlob(filePath, file);

          if (response?.presignURL) {
            store.setState(
              produce((state) => {
                const storyIndex = state.stories.findIndex(
                  (story) => story.id === storyId,
                );
                if (storyIndex === -1) {
                  throw new Error("Story not found");
                }

                state.stories[storyIndex].script[item.id].visualUrl =
                  response.presignURL;
                state.stories[storyIndex].script[item.id].visualSeed = null;
                state.stories[storyIndex].script[item.id].visuals =
                  state.stories[storyIndex].script[item.id]?.visuals ?? [];
                state.stories[storyIndex].script[item.id].visuals.push({
                  url: response.presignURL,
                  seed: null,
                  path: filePath,
                  prompt: null,
                });

                state.stories[storyIndex].script[item.id].visual =
                  state.stories[storyIndex].script[item.id].visuals.length - 1;
              }),
            );

            store.getState().saveStory(storyId);
          }
        });
      }
    },
    [storyId, item.id],
  );

  // useEffect(() => {
  //   if (visualsListRef.current) {
  //     visualsListRef.current.scrollLeft =
  //       visualsListRef.current.scrollWidth - visualsListRef.current.clientWidth;
  //     console.log(visualsListRef.current);
  //   }
  // }, [item?.visuals]);

  return (
    <CustomDialog padding="none">
      <div
        className={style({
          display: "flex",
          flexDirection: {
            default: "column",
            sm: "row",
          },
          size: "full",
        })}
      >
        <div
          ref={visualsListRef}
          className={style({
            flexGrow: 1,
            display: "flex",
            gap: 8,
            minHeight: {
              default: 0,
              sm: "56.25vh",
            },
            aspectRatio: "16 / 10",
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
          })}
        >
          {item?.visuals?.map((visual, idx) => (
            <StoryCardVisual
              key={idx}
              visual={visual}
              item={item}
              idx={idx}
              onPress={() => {
                updateStoryScriptItem(story.id, item.id, {
                  visual: idx,
                  visualUrl: visual.url,
                  visualPrompt: visual.prompt,
                });
              }}
              onDelete={() => {
                store.getState().updateStoryScriptItem(story.id, item.id, {
                  visuals: item?.visuals?.toSpliced(idx, 1) ?? [],
                });
              }}
            />
          ))}
          {!item?.visuals?.length && (
            <Skeleton isLoading={true}>
              <Image
                src="https://react-spectrum.adobe.com/s2/preview.c3b340d3.png"
                className={style({
                  flexGrow: 0,
                  flexShrink: 0,
                  flexBasis: "100%",
                  display: "flex",
                })}
              />
            </Skeleton>
          )}
        </div>

        <div
          className={style({
            padding: {
              default: 24,
              sm: 32,
            },
            //   flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            flexBasis: {
              default: "auto",
              sm: 320,
            },
            flexShrink: 0,
            rowGap: 32,
          })}
        >
          <div
            className={style({ display: "flex", alignItems: "center", gap: 8 })}
          >
            <Text styles={detailStyles}>Personas:</Text>
            <AvatarGroup size={24} aria-label="Personas">
              {[...selectedPersonas].map((persona, idx) => (
                <PersonaAvatar
                  key={`${persona?.id}-${idx}`}
                  persona={persona}
                  isOverBackground
                />
              ))}
            </AvatarGroup>
            <MenuTrigger>
              <ActionButton
                aria-label="Select personas"
                isQuiet
                isDisabled={isGenerating || isRegenerateVisualPending}
              >
                <MoreIcon />
              </ActionButton>
              <Menu
                selectionMode="multiple"
                selectedKeys={selectedPersonaKeys}
                onSelectionChange={setSelectedPersonaKeys}
                aria-label="Select personas"
              >
                {story?.personas?.map?.((persona, idx) => (
                  <MenuItem key={idx} id={persona?.id ?? idx}>
                    <PersonaAvatar
                      persona={persona}
                      size={20}
                      isOverBackground
                    />
                    <Text>{persona?.name}</Text>
                    <Keyboard>{persona?.role}</Keyboard>
                  </MenuItem>
                ))}
              </Menu>
            </MenuTrigger>
          </div>
          <div>
            <div className={style({ font: "detail-sm", marginBottom: 4 })}>
              <Text>{item.section}</Text>
            </div>
            <div className={style({ marginBottom: 8 })}>
              {/* <Text slot="title">{item.title}</Text> */}
              <TextField
                label="Title"
                value={title}
                onChange={setTitle}
                isDisabled={isGenerating || isRegenerateVisualPending}
                size="S"
                onBlur={handleTextUpdate}
              />
            </div>
            <div
              className={style({
                font: "detail",
                whiteSpace: "pre-line",
              })}
            >
              {/* <Text>{item.text}</Text> */}
              <TextArea
                label="Text"
                value={text}
                onChange={setText}
                isDisabled={isGenerating || isRegenerateVisualPending}
                size="S"
                onBlur={handleTextUpdate}
              />
            </div>
            {/* {!!item?.solutions?.length && (
              <TagGroup
                selectionMode="none"
                size="S"
                aria-label="Solutions"
                styles={style({ marginTop: 8 })}
              >
                {item?.solutions?.map?.((solution, idx) => (
                  <Tag key={`${solution}-${idx}`}>{solution}</Tag>
                ))}
              </TagGroup>
            )} */}
          </div>
          <ActionButton
            isQuiet
            isDisabled={isGenerating || isRegenerateVisualPending}
            onPress={() =>
              regenerateVisualContext(story.id, item.id, {
                personas: [...selectedPersonaKeys],
              })
            }
            styles={style({ marginStart: "auto", marginBottom: -48 })}
          >
            <AIGenerateIcon />
            <Text>Re-generate prompt</Text>
          </ActionButton>
          <TextArea
            label="Prompt"
            value={prompt}
            isDisabled={isGenerating || isRegenerateVisualPending}
            onChange={setPrompt}
          />
          <div
            className={style({ display: "flex", alignItems: "center", gap: 8 })}
          >
            {!!story?.imageReference?.url && (
              <TooltipTrigger>
                <ActionButton isQuiet UNSAFE_className={style({ padding: 0 })}>
                  <Image
                    src={story?.imageReference?.url}
                    alt="Generated visual"
                    styles={style({
                      borderRadius: "default",
                      borderColor: "transparent-white-75",
                      borderWidth: 2,
                      borderStyle: "solid",
                      size: 32,
                    })}
                  />
                </ActionButton>
                <Tooltip>
                  Reference style: {story?.imageReference?.name}
                </Tooltip>
              </TooltipTrigger>
            )}
            <ButtonGroup styles={style({ marginStart: "auto" })}>
              <FileTrigger
                acceptedFileTypes="image/*"
                onSelect={handleImageSelect}
              >
                <ActionButton isQuiet isDisabled={isUploading}>
                  {!isUploading ? (
                    <UploadToCloudIcon />
                  ) : (
                    <ProgressCircle
                      isIndeterminate
                      size="S"
                      staticColor="auto"
                      styles={style({ alignSelf: "center", marginStart: -4 })}
                    />
                  )}
                </ActionButton>
              </FileTrigger>
              <TooltipTrigger>
                <Button
                  variant="primary"
                  isDisabled={
                    isGenerating || isRegenerateVisualPending || !prompt
                  }
                  onPress={() => browseVisuals(story)}
                >
                  <BrowseIcon />
                  <Text>Browse</Text>
                </Button>
                <Tooltip>Pick different image from the library</Tooltip>
              </TooltipTrigger>
              <TooltipTrigger>
                <Button
                  variant="primary"
                  isDisabled={
                    isGenerating ||
                    isRegenerateVisualPending ||
                    !prompt ||
                    !item?.visualSeed
                  }
                  onPress={() =>
                    generateSimilarVisual(story.id, item.id, prompt)
                  }
                >
                  <SimilarIcon />
                  <Text>Similar</Text>
                </Button>
                <Tooltip>Generate similar image to the current one</Tooltip>
              </TooltipTrigger>
              <TooltipTrigger>
                <Button
                  variant="accent"
                  isDisabled={
                    isGenerating || isRegenerateVisualPending || !prompt
                  }
                  onPress={() => regenerateVisual(story.id, item.id, prompt)}
                >
                  <AIGenerateIcon />
                  <Text>Generate</Text>
                </Button>
                <Tooltip>Generate new image based on provided prompt</Tooltip>
              </TooltipTrigger>
            </ButtonGroup>
          </div>
        </div>

        <CloseButton
          // staticColor="black"
          styles={style({
            position: "absolute",
            top: 12,
            insetEnd: 12,
          })}
        />
      </div>

      <DialogContainer
        onDismiss={() => setIsBrowseDialogOpen(false)}
        styles={style({ zIndex: 20 })}
      >
        {isBrowseDialogOpen && (
          <ImageSelectionDialog
            libraryId={story?.personas[0]?.libraryId}
            onPrimaryAction={handleImageConfirm}
          />
        )}
      </DialogContainer>
    </CustomDialog>
  );
};
